import { pipe } from 'lib/utils';

import { ImportanceEnum } from './getApiLayer.types';
import { parseRetryAfterUncapped, RETRY_AFTER_MAX_MS } from './rateLimitRetry';
import {
  mergeArrays,
  appendUrlQueryString,
  replaceVariables,
  checkEndPointId,
  mergeEndPointOptions,
} from './utils';

import type {
  InitType,
  GetInterceptorsType,
  CustomFetchOptionsType,
  SetEndPointOptionsType,
  GetApiLayerOptionsType,
  InterceptorsKeyType,
  ApiErrorType,
  ComposedReturnType,
  SetEndPointExtendedOptionsType,
  SetEndpointReturnType,
} from './';

const ensureError = (e: unknown): Error => {
  if (e instanceof Error) return e;
  const error = new Error(
    `API Error: ${typeof e === 'object' ? JSON.stringify(e) : String(e)}`,
  );
  if (e && typeof e === 'object') {
    Object.assign(error, e);
  }
  return error;
};

const DEFAULT_GLOBAL_OPTIONS = {
  retryTimes: 0,
  retryStatus: [],
  rateLimitRetryDelaysMs: [] as readonly number[],
};

const TOO_MANY_REQUESTS = 429;

// Cancelling during the backoff has to look like cancelling during the fetch,
// because callers tell the two apart by name rather than by type:
// intelCards.model.ts swallows an error named 'AbortError' and reports anything
// else, and SKIP_ERRORS does the same. Rejecting with the 429 that started the
// backoff would surface a deliberate cancellation as a real request failure.
//
// The signal's own reason is used when it is an Error, which is what fetch
// rejects with. It is not always: a stubbed controller may carry none, and a
// DOMException is not an Error on every runtime, where ensureError would
// rebuild it and drop the name off the prototype.
const abortReason = (signal?: AbortSignal): Error => {
  const reason: unknown = signal?.reason;

  if (reason instanceof Error) return reason;

  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';

  return error;
};

// Resolves true when the wait completed, false when the caller aborted during
// it. Without this an abort() mid-backoff leaves the request pending for the
// rest of the delay and then fires a replay on a signal that is already dead.
const sleepUnlessAborted = (
  ms: number,
  signal?: AbortSignal,
): Promise<boolean> =>
  new Promise((resolve) => {
    // Some suites install a stubbed AbortController whose signal is not an
    // event target, so fall back to a plain wait rather than throwing.
    if (typeof signal?.addEventListener !== 'function') {
      setTimeout(() => resolve(true), ms);
      return;
    }

    if (signal.aborted) {
      resolve(false);
      return;
    }

    // The handler has to be able to clear a timer that does not exist yet, and
    // the timer has to be able to detach the handler, so one of them is reached
    // through a holder rather than directly.
    const pending: { timer?: ReturnType<typeof setTimeout> } = {};

    const onAbort = () => {
      clearTimeout(pending.timer);
      resolve(false);
    };

    pending.timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);

    signal.addEventListener('abort', onAbort, { once: true });
  });

const DEFAULT_ENDPOINT_OPTIONS = {
  ignoreGlobalInterceptors: false,
  importance: ImportanceEnum.high,
};

const fetchWrapper =
  <ReturnType>(
    beforeHooks: (args: [RequestInfo, InitType?]) => [RequestInfo, InitType?],
    afterHooks: (
      args: ComposedReturnType<ReturnType>,
    ) => ComposedReturnType<ReturnType>,
  ) =>
  (
    ...options: [RequestInfo, InitType?]
  ): Promise<ComposedReturnType<ReturnType>> => {
    return fetch
      .apply(globalThis, beforeHooks(options))
      .then(async (response) => {
        const { headers, status, ok } = response;

        if (status === 204) {
          return {
            data: {},
            ok,
            headers,
            status,
          };
        }

        try {
          if (headers.get('content-type')?.includes('text/html')) {
            const data = await response.text().catch((error) => {
              // eslint-disable-next-line no-console
              console.error(error);
            });
            return {
              data,
              ok,
              headers,
              status,
            };
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }

        const data = await response.json().catch((error) => {
          // TODO: Revert this change after migrating all async functions to trycatch block
          // eslint-disable-next-line no-console
          console.error(error);
          // throw new Error(error);
        });

        return {
          data,
          ok,
          headers,
          status,
        };
      })
      .then((data) => afterHooks(data));
  };

const getInterceptors = <ReturnType>({
  endPointOptions,
  globalOptions,
  interceptorsKey,
}: GetInterceptorsType<ReturnType>) => {
  return endPointOptions.ignoreGlobalInterceptors
    ? (endPointOptions.globals?.interceptor?.[interceptorsKey] ?? [])
    : mergeArrays(
        globalOptions.interceptor?.[interceptorsKey],
        endPointOptions.globals?.interceptor?.[interceptorsKey],
      );
};

const customFetch = <PathType, ParamsType, QueryType, ReturnType>(
  globalOptions: CustomFetchOptionsType,
  endPointOptions: SetEndPointOptionsType<ReturnType>,
  abortController: AbortController,
  path?: PathType,
  params?: ParamsType,
  query?: QueryType,
): Promise<ComposedReturnType<ReturnType>> => {
  const getHook = (interceptorsKey: InterceptorsKeyType) =>
    getInterceptors({
      interceptorsKey,
      endPointOptions,
      globalOptions,
    });

  const beforeHooks = pipe<[RequestInfo, InitType?]>(getHook('before'));
  const afterHooks = pipe<ComposedReturnType<ReturnType>>(getHook('after'));
  const errorHandlerHooks = pipe<ApiErrorType>(getHook('errorHandler'));

  const fetchOptions = {
    headers: {
      ...globalOptions.headers,
      ...endPointOptions.globals?.headers,
    },
    method: endPointOptions.method,
    importance: endPointOptions.importance,
    'content-type':
      endPointOptions.globals?.contentType ?? globalOptions.contentType,
    body: JSON.stringify(params),
    signal: abortController.signal,
  } as unknown as InitType;

  if (endPointOptions.method === 'GET') delete fetchOptions?.body;

  return new Promise((resolve, reject) => {
    let retryAttempts = 1;
    let rateLimitAttempts = 0;
    const retryTimes =
      endPointOptions.globals?.retryTimes ?? globalOptions.retryTimes;
    const retryStatus =
      endPointOptions.globals?.retryStatus ?? globalOptions.retryStatus;
    const rateLimitDelays =
      endPointOptions.globals?.rateLimitRetryDelaysMs ??
      globalOptions.rateLimitRetryDelaysMs ??
      [];

    const handleFetchError = async (e: ApiErrorType) => {
      // Takes an Error as well, because the abort path below rejects with one.
      // ApiErrorType ends in a Record<string, unknown>, which a class instance
      // is not assignable to however well it fits at runtime, so the hooks take
      // it through a cast rather than the value being reshaped to please them.
      const runErrorHandlerHooksAndReject = (error: ApiErrorType | Error) => {
        errorHandlerHooks(error as ApiErrorType);
        return reject(ensureError(error));
      };

      // A 429 is the rate limiter refusing the request at the middleware, before
      // the server application runs, so nothing happened upstream and a replay
      // cannot duplicate anything. That is what separates this from the generic
      // retry below, which is why this one is safe on POST and PUT too.
      //
      // Counted separately from retryAttempts so a throttled request does not
      // spend an endpoint's generic retry budget, and so an endpoint that opts
      // out of generic retries still backs off when it is throttled.
      if (
        Number(e.status) === TOO_MANY_REQUESTS &&
        rateLimitAttempts < rateLimitDelays.length
      ) {
        // The server's Retry-After is the moment its window resets, so it beats
        // any ladder step we could guess. statusErrorInterceptor carries the
        // original Response through, which is the only place that header
        // survives.
        const { headers } = (e.response ?? {}) as { headers?: Headers };
        const hint = parseRetryAfterUncapped(
          headers?.get('Retry-After') ?? null,
        );

        // Longer than a page should hold a request open. Replaying before the
        // window resets is a guaranteed second 429, so give up now rather than
        // spend attempts learning that.
        if (hint !== null && hint > RETRY_AFTER_MAX_MS) {
          return runErrorHandlerHooksAndReject(e);
        }

        const slept = await sleepUnlessAborted(
          hint ?? rateLimitDelays[rateLimitAttempts]!,
          abortController.signal,
        );

        if (!slept) {
          return runErrorHandlerHooksAndReject(
            abortReason(abortController.signal),
          );
        }

        rateLimitAttempts++;

        return runFetch();
      }

      if (retryAttempts >= retryTimes) {
        return runErrorHandlerHooksAndReject(e);
      }

      const isEmptyRetryStatus = !retryStatus.length;
      const hasErrorStatusOrErrorCode =
        retryStatus.includes(e.status ?? '') ||
        retryStatus.includes(e.code ?? '');

      if (hasErrorStatusOrErrorCode || isEmptyRetryStatus) {
        retryAttempts++;
        return runFetch();
      }

      return runErrorHandlerHooksAndReject(e);
    };

    const runFetch = async () => {
      const url =
        (endPointOptions.globals?.baseUrl ?? globalOptions.baseUrl) +
        endPointOptions.path;

      const queryParamBracketNotation =
        endPointOptions.globals?.queryParamBracketNotation ??
        globalOptions.queryParamBracketNotation;

      const urlToFetch = pipe<string>([
        (url) =>
          // TODO: Fix issue with QueryType
          appendUrlQueryString(
            url,
            query as unknown as Record<string, string>,
            queryParamBracketNotation,
          ),
        (url) =>
          replaceVariables(url, path as unknown as Record<string, string>),
      ])(url);

      await fetchWrapper<ReturnType>(beforeHooks, afterHooks)(
        urlToFetch,
        fetchOptions,
      )
        .then(resolve)
        .catch(handleFetchError);
    };

    runFetch().catch((e) => reject(ensureError(e)));
  });
};

const getApiLayer = (globalOptions: GetApiLayerOptionsType) => {
  const abortControllerMap = new Map();

  const setEndpoint = <PathType, ParamsType, QueryType = any, ReturnType = any>(
    endPointOptions: SetEndPointOptionsType<ReturnType>,
  ): SetEndpointReturnType<PathType, ParamsType, QueryType, ReturnType> => {
    const { id, namespace } = endPointOptions;
    const abortController = new AbortController();
    const globalOptionsData = { ...DEFAULT_GLOBAL_OPTIONS, ...globalOptions };
    const endpointOptionsData = {
      ...DEFAULT_ENDPOINT_OPTIONS,
      ...endPointOptions,
    };

    abortControllerMap.set(id, abortController);

    if (namespace) {
      const allIdsInNamespace = abortControllerMap.get(namespace) ?? [];
      const namespaceIds = [...Array.from(allIdsInNamespace), id];
      abortControllerMap.set(namespace, namespaceIds);
    }

    const endpoint = (options?: {
      path?: PathType;
      params?: ParamsType;
      query?: QueryType;
    }) =>
      customFetch<PathType, ParamsType, QueryType, ReturnType>(
        globalOptionsData,
        endpointOptionsData,
        abortControllerMap.get(id),
        options?.path,
        options?.params,
        options?.query,
      );

    const extendEndpoint = <
      ExtendedPathType = PathType,
      ExtendedParamsType = ParamsType,
      ExtendedQueryType = QueryType,
      ExtendedReturnType = ReturnType,
    >(
      extendedEndPointOptions: SetEndPointExtendedOptionsType<ExtendedReturnType>,
    ) => {
      return setEndpoint<
        ExtendedPathType,
        ExtendedParamsType,
        ExtendedQueryType,
        ExtendedReturnType
      >(
        mergeEndPointOptions<ExtendedReturnType, ReturnType>(
          endPointOptions,
          extendedEndPointOptions,
        ),
      );
    };

    return [endpoint, extendEndpoint];
  };

  const abort = (id: string) => {
    const abortController = abortControllerMap.get(id);

    const resetAbortController = (controllerId: string) =>
      abortControllerMap.set(controllerId, new AbortController());

    if (Array.isArray(abortController)) {
      abortController.forEach((abortId) => {
        abortControllerMap.get(abortId).abort();
        resetAbortController(abortId);
      });
    } else {
      abortController.abort();
      resetAbortController(id);
    }
  };

  return {
    setEndpoint: checkEndPointId(setEndpoint, () => abortControllerMap),
    abort,
  };
};

export default getApiLayer;
